import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  where,
  Timestamp,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { User } from './authService';
import { userDeletionService } from './userDeletionService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

export interface Business {
  id: string;
  name: string;
  address: string;
  email: string;
  managerId: string;
  status: 'pending' | 'approved' | 'rejected' | 'active';
  logoUrl?: string;
  gmbId?: string;
  createdAt: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export interface AdminStats {
  totalUsers: number;
  totalBusinesses: number;
  pendingBusinesses: number;
  approvedBusinesses: number;
  rejectedBusinesses: number;
  usersMarkedForDeletion: number;
}

class AdminService {
  // Check if user has admin privileges
  isAdmin(user: User | null): boolean {
    return user?.systemRole === 'admin';
  }

  // Get all users
  async getAllUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as User[];
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  }

  // Get all businesses
  async getAllBusinesses(): Promise<Business[]> {
    try {
      const businessesRef = collection(db, 'businesses');
      const q = query(businessesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Business[];
    } catch (error) {
      console.error('Error fetching all businesses:', error);
      throw error;
    }
  }

  // Get admin dashboard stats
  async getAdminStats(): Promise<AdminStats> {
    try {
      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => doc.data()) as User[];
      
      // Get all businesses
      const businessesSnapshot = await getDocs(collection(db, 'businesses'));
      const businesses = businessesSnapshot.docs.map(doc => doc.data()) as Business[];

      return {
        totalUsers: users.length,
        totalBusinesses: businesses.length,
        pendingBusinesses: businesses.filter(b => b.status === 'pending').length,
        approvedBusinesses: businesses.filter(b => b.status === 'approved').length,
        rejectedBusinesses: businesses.filter(b => b.status === 'rejected').length,
        usersMarkedForDeletion: users.filter(u => u.markedForDeletion).length,
      };
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      throw error;
    }
  }
  // Update business status
  async updateBusinessStatus(businessId: string, status: 'pending' | 'approved' | 'rejected' | 'active'): Promise<void> {
    try {
      const businessRef = doc(db, 'businesses', businessId);
      await updateDoc(businessRef, {
        status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating business status:', error);
      throw error;
    }
  }

  // Update user system role
  async updateUserSystemRole(userId: string, systemRole: 'admin' | null): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const updateData: any = {
        updatedAt: serverTimestamp()
      };

      if (systemRole) {
        updateData.systemRole = systemRole;
      } else {
        // Remove system role field
        updateData.systemRole = null;
      }

      await updateDoc(userRef, updateData);
    } catch (error) {
      console.error('Error updating user system role:', error);
      throw error;
    }
  }

  // Update user information (admin only)
  async updateUser(userId: string, userData: Partial<User>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const updateData = {
        ...userData,
        updatedAt: serverTimestamp()
      };

      // Remove fields that shouldn't be updated directly
      delete updateData.id;
      delete updateData.createdAt;

      // Check if verification status is being updated and set isVerified accordingly
      if ('emailVerified' in userData || 'phoneVerified' in userData) {
        // Get current verification status from existing user data
        // We need to check what the current values are first
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const currentUserData = userDoc.exists() ? userDoc.data() : {};
        
        // Determine the new verification status
        const newEmailVerified = 'emailVerified' in userData ? userData.emailVerified : (currentUserData?.emailVerified || false);
        const newPhoneVerified = 'phoneVerified' in userData ? userData.phoneVerified : (currentUserData?.phoneVerified || false);
        
        // Set isVerified to true if either email or phone is verified
        updateData.isVerified = newEmailVerified || newPhoneVerified;
      }

      // Update user document
      await updateDoc(userRef, updateData);

      // Check if verification status fields are being updated
      if ('emailVerified' in userData || 'phoneVerified' in userData) {
        // Get reference to otpVerification document
        const otpVerificationRef = doc(db, 'otpVerification', userId);
        
        // Create update object for OTP verification status
        const otpUpdateData: any = {};
        
        // Set email verification status if it's being updated
        if ('emailVerified' in userData) {
          // Generate a sample 6-digit code
          const emailCode = Math.floor(100000 + Math.random() * 900000).toString();
          
          // Set expiry date to tomorrow
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          otpUpdateData.email = {
            attempts: 0,
            verified: userData.emailVerified,
            expiresAt: tomorrow.toISOString(),
            code: emailCode
          };
        }
        
        // Set phone verification status if it's being updated
        if ('phoneVerified' in userData) {
          // Generate a sample 6-digit code
          const phoneCode = Math.floor(100000 + Math.random() * 900000).toString();
          
          // Set expiry date to tomorrow
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          otpUpdateData.phone = {
            attempts: 0,
            verified: userData.phoneVerified,
            expiresAt: tomorrow.toISOString(),
            code: phoneCode
          };
        }
        
        try {
          // Update the otpVerification document
          await updateDoc(otpVerificationRef, otpUpdateData);
          console.log('OTP verification status updated successfully');
        } catch (error) {
          // Check if the error is because the document doesn't exist
          const otpError = error as { code?: string };
          
          if (otpError.code === 'not-found') {
            console.log('OTP verification document not found, creating new one');
            // Use setDoc instead of set on DocumentReference
            const { setDoc } = await import('firebase/firestore');
            await setDoc(otpVerificationRef, otpUpdateData);
          } else {
            console.error('Error updating OTP verification:', error);
            // Continue with the function, don't throw error as this is a secondary update
          }
        }
      }
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Delete user (admin only) - Now performs soft delete with Firebase Auth deletion
  async deleteUser(userId: string, deletionReason?: string): Promise<void> {
    try {
      // First get the user data before deletion
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('__name__', '==', userId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        throw new Error('User not found');
      }

      const userData = snapshot.docs[0].data();

      // Call Firebase Function to delete from Authentication
      const deleteUserAuth = httpsCallable(functions, 'deleteUserFromAuth');
      await deleteUserAuth({ userId });

      // Perform soft delete (removes from Firestore and related data)
      await userDeletionService.softDeleteUser(userId, userData, deletionReason);
      
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Delete business (admin only)
  async deleteBusiness(businessId: string): Promise<void> {
    try {
      const businessRef = doc(db, 'businesses', businessId);
      await deleteDoc(businessRef);
    } catch (error) {
      console.error('Error deleting business:', error);
      throw error;
    }
  }

  // Get pending businesses
  async getPendingBusinesses(): Promise<Business[]> {
    try {
      const businessesRef = collection(db, 'businesses');
      const q = query(businessesRef, where('status', '==', 'pending'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Business[];
    } catch (error) {
      console.error('Error fetching pending businesses:', error);
      throw error;
    }
  }
  // Get users marked for deletion
  async getUsersMarkedForDeletion(): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('markedForDeletion', '==', true));
      const snapshot = await getDocs(q);
      
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as User[];
      
      // Sort by deletionRequestedAt in code if the field exists
      return users.sort((a, b) => {
        if (!a.deletionRequestedAt || !b.deletionRequestedAt) return 0;
        return b.deletionRequestedAt.toMillis() - a.deletionRequestedAt.toMillis();
      });
    } catch (error) {
      console.error('Error fetching users marked for deletion:', error);
      throw error;
    }
  }
}

export const adminService = new AdminService();
